import React, { useState } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Icon, Label } from 'semantic-ui-react'
import styled from 'styled-components'
import { CardLabel, CardValue } from '~/components/DataCard'
import BankAccountDrawer, { BankAccount } from './BankAccountDrawer'
import CustomIcon from '~/components/CustomIcon'

const BankIcon = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: hsla(var(--primary), 0.1);
  color: hsl(var(--primary));
`

const AccountCard = styled.div`
  backdrop-filter: blur(4px);
  width: 400px;
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 10px;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  padding: 1rem;
  transition: all 0.3s ease;
  animation: fadeIn 0.6s ease-out;
  margin-bottom: 1rem;

  &:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
`

const CardInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const StatusBadge = styled(Label)`
  &.ui.label {
    border-radius: 20px !important;
    font-size: 0.75rem !important;
    padding: 0.35rem 0.75rem !important;
    font-weight: 500 !important;
  }
`

const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const SwiftRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  margin-top: 0.75rem;
`

const CardActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  margin-top: 0.75rem;
`

const ActionButton = styled(Button)`
  &.ui.button {
    background: transparent !important;
    width: 300px;
    min-height: 1em;
    border: 1px solid #e2e8f0 !important;
    border-radius: 0.375rem !important;
    padding: 0.5rem 1.5rem !important;
    color: #64748b !important;
    font-size: 1rem !important;

    &:hover {
      background: #f8fafc !important;
    }
  }
`

const DeleteButton = styled(Button)`
  &.ui.button {
    background: transparent !important;
    border: 1px solid #e2e8f0 !important;
    border-radius: 0.375rem !important;
    padding: 0.5rem !important;
    color: #64748b !important;

    &:hover {
      background: #fef2f2 !important;
      border-color: #fecaca !important;
      color: #dc2626 !important;
    }
  }
`
const BankDetails: React.FC = () => {
  const { t } = useTranslation()
  const { control, setValue, getValues } = useFormContext()

  const { fields, append, update, remove } = useFieldArray({
    control,
    name: 'accounts',
    keyName: 'key'
  })

  const watchedAccounts = useWatch({
    control,
    name: 'accounts'
  }) as BankAccount[] | undefined

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [editingAccount, setEditingAccount] = useState<BankAccount | null>(null)

  function handleAddNew() {
    setEditingIndex(null)
    setEditingAccount(null)
    setDrawerOpen(true)
  }

  function handleEdit(index: number) {
    const account = getValues(`accounts.${index}`)
    setEditingIndex(index)
    setEditingAccount(account)
    setDrawerOpen(true)
  }

  function handleCloseDrawer() {
    setDrawerOpen(false)
    setEditingIndex(null)
    setEditingAccount(null)
  }

  function handleSave(data: BankAccount) {
    if (editingIndex !== null) {
      update(editingIndex, data)
    } else {
      append(data)
    }
    handleCloseDrawer()
  }

  function handleDelete(index: number) {
    remove(index)
  }

  function handleToggleStatus(index: number) {
    const currentStatus = getValues(`accounts.${index}.status`)
    setValue(`accounts.${index}.status`, currentStatus === 1 ? 0 : 1)
  }

  function renderAccountCard(field: any, index: number) {
    const account = watchedAccounts?.[index] || (getValues(`accounts.${index}`) as BankAccount)
    if (!account) return null

    return (
      <AccountCard>
        <CardHeader>
          <CardInfo>
            <BankIcon>
              <CustomIcon name='building' style={{ fontSize: '2em' }} />
            </BankIcon>
            <div>
              <CardValue style={{ marginBottom: '0.25rem' }}>{account.name}</CardValue>
              <CardLabel style={{ marginBottom: 0 }}>
                IBAN:<br />
                {account.iban}
              </CardLabel>
            </div>
          </CardInfo>
          <ToggleWrapper>
            <div
              onClick={() => handleToggleStatus(index)}
              style={{
                width: '44px',
                height: '24px',
                borderRadius: '12px',
                backgroundColor: account.status === 1 ? '#1a3175' : '#cbd5e1',
                cursor: 'pointer',
                position: 'relative',
                transition: 'background-color 0.2s'
              }}
            >
              <div
                style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  position: 'absolute',
                  top: '2px',
                  left: account.status === 1 ? '22px' : '2px',
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                }}
              />
            </div>
            <StatusBadge color={account.status === 1 ? 'blue' : 'grey'}>
              {account.status === 1 ? t('Active') : t('Inactive')}
            </StatusBadge>
          </ToggleWrapper>
        </CardHeader>

        <SwiftRow>
          <CardLabel style={{ marginBottom: 0 }}>SWIFT/BIC:</CardLabel>
          <CardValue>{account.swift}</CardValue>
        </SwiftRow>

        <CardActions>
          <ActionButton type='button' onClick={() => handleEdit(index)}>
            <Icon name='edit outline' />
            {t('Edit')}
          </ActionButton>
          <DeleteButton type='button' icon onClick={() => handleDelete(index)}>
            <Icon name='trash alternate outline' />
          </DeleteButton>
        </CardActions>
      </AccountCard>
    )
  }

  return (
    <div style={{ margin: 20 }}>

      <div style={{ display: 'flex', justifyContent: 'end' }}>
        <Button style={{ color: 'white' }} type='button' onClick={handleAddNew}>
          <Icon name='plus' />
          {t('Add bank account')}
        </Button>
      </div>

      <div style={{ display: 'flex', gap: 50, flexWrap: 'wrap' }}>
        {fields.map((field, index) => renderAccountCard(field, index))}
      </div>

      <BankAccountDrawer
        isVisible={drawerOpen}
        account={editingAccount}
        onClose={handleCloseDrawer}
        onSave={handleSave}
      />
    </div>
  )
}

export default BankDetails
